import { useState } from "react";
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Transcription } from "@/hooks/dashboard/useTranscription";

const ArrowDownSvg = () => (
  <svg width="24" height="11" viewBox="0 0 24 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1L12 10L23 1" stroke="#6D6D6D" />
  </svg>
);

const ArrowUpSvg = () => (
  <svg width="24" height="11" viewBox="0 0 24 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 10L12 1L23 10" stroke="#6D6D6D" />
  </svg>
);


export const ExpandableText = ({
  title,
  text,
  readOnly = true,
  onSave,
  showActions = true,
}: {
  title: string;
  text: string;
  readOnly?: boolean;
  onSave?: (transcription: Transcription) => void;
  showActions?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const hasText = text.trim().length > 0;
  
  // Calculate classes for better readability
  const getTextareaClasses = () => {
    let baseClasses = "focus:outline-none w-full focus:ring-0 px-6 font-sans font-normal hover:bg-gray-50 transition-all border-2 border-transparent text-sm leading-relaxed resize-none placeholder:truncate placeholder:whitespace-nowrap placeholder:overflow-hidden";
    
    if (hasText) {
      if (isExpanded) {
        baseClasses += " custom-scrollbar pt-8 pb-12 h-[150px] rounded-2xl overflow-y-auto";
      } else {
        baseClasses += " no-scrollbar pt-8 pb-10 h-[80px] rounded-2xl overflow-y-hidden";
      }
    } else {
      baseClasses += " pt-3 pb-3 h-[49px] rounded-full overflow-hidden cursor-not-allowed";
    }
    
    return baseClasses;
  };

  return (
    <div className="relative w-full  mx-auto">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 1px;
          height: 1px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 0;
          margin: 50px 0 50px 0;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #D1D5DB;
          border-radius: 0;
          min-height: 30px;
          border: none;
          box-shadow: none;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: #9CA3AF;
        }
        .custom-scrollbar::-webkit-scrollbar-corner {
          background: transparent;
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #D1D5DB transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-button {
          display: none;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      {/* Save button */}
      {hasText && showActions && (
        <button
          onClick={() => onSave?.({ title: title, transcription: text, user_email: "", created_at: new Date().toISOString() })}
          className="absolute top-0 right-10 text-gray-600 bg-transparent focus:outline-none border-none transition"
          title="Save"
        >
          <SaveIcon fontSize="small" />
        </button>
      )}

      {/* Copy button */}
      {hasText && showActions && (
        <button
          onClick={handleCopy}
          className="absolute top-0 right-0 text-gray-600 bg-transparent focus:outline-none border-none transition"
          title="Copy"
        >
          <ContentCopyIcon fontSize="small" />
        </button>
      )}

      {/* Text area with gradient border restored */}
      <textarea
        value={text}
        readOnly={readOnly}
        placeholder="Upload a video & click generate for transcription..."
        className={getTextareaClasses()}
        style={{
          background:
            "linear-gradient(white, white) padding-box, linear-gradient(#F39A81, #959BEA) border-box",
          border: "2px solid transparent"
        }}
      />


      {/* Expand/Collapse only if text exists */}
      {hasText && (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-center rounded-full bg-[#D9D9D999] text-black border-none focus:outline-none shadow-sm hover:opacity-90 transition h-[17px] w-[70px]"
          >
            {isExpanded ? (
              <ArrowUpSvg />
            ) : (
              <ArrowDownSvg />
            )}
          </button>
        </div>
      )}

      {/* Copy feedback */}
      {copied && (
        <span className="absolute top-3 right-20 text-xs text-green-600">
          Copied!
        </span>
      )}
    </div>
  );
};
